import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase.config';
import Papa from 'papaparse';
import {
  ArrowLeft,
  Clock
} from 'lucide-react';
import ClassroomTable from '../../components/availability/ClassroomTable';

const parseTimeToMinutes = (timeStr) => {
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let totalMinutes = hours * 60 + minutes;
  if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
  if (period === 'AM' && hours === 12) totalMinutes = minutes;
  return totalMinutes;
};

const parseRoomNumber = (room) => {
  const match = room.match(/(\d+)([A-Z]+)(\d+)/);
  if (!match) return room;
  const [, floor, section, number] = match;
  return `${floor.padStart(2, '0')}${section}${number.padStart(2, '0')}`;
};

const ClassroomsByBuilding = () => {
  const { building } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState({});
  const [selectedTimeRange, setSelectedTimeRange] = useState(() => {
    const saved = localStorage.getItem('selectedTimeRange');
    return saved ? JSON.parse(saved) : null;
  });

  const formatTimeDisplay = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  useEffect(() => {
    const fetchRoomsAndSchedules = async () => {
      try {
        const decodedBuilding = decodeURIComponent(building);
        
        // Fetch lecture halls for this building from Supabase
        const { data: lectureHalls, error } = await supabase
          .from('LectureHall')
          .select(`
            *,
            building:Building(*),
            photos:Photo(*)
          `)
          .eq('building.name', decodedBuilding);

        if (error) throw error;

        // Filter results (Supabase doesn't fully support nested eq on joins)
        const filteredHalls = lectureHalls.filter(
          hall => hall.building?.name === decodedBuilding
        );

        // Transform to expected format
        const roomsData = filteredHalls.map(hall => ({
          id: hall.id,
          building: hall.building?.name || decodedBuilding,
          room: hall.room,
          photos: hall.photos?.map(p => p.url) || [],
          hasAvInputs: hall.hasAvInputs,
          hasPc: hall.hasPc,
          hasWhiteboard: hall.hasWhiteboard,
          hasProjector: hall.hasProjector,
          information: {
            av_inputs: hall.hasAvInputs ? 'yes' : 'no',
            pc: hall.hasPc ? 'yes' : 'no',
            whiteboard: hall.hasWhiteboard ? 'yes' : 'no',
            projector: hall.hasProjector ? 'yes' : 'no'
          }
        }));

        setRooms(roomsData);

        // Fetch and parse course schedule
        const response = await fetch('/data/cleaned_courses_f26.csv');
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const currentTime = new Date();
            const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
            const timeToCheck = selectedTimeRange ? selectedTimeRange.start : 
              (currentTime.getHours() * 60 + currentTime.getMinutes());
            const duration = selectedTimeRange?.duration || 0;
            
            const roomAvailability = {};

            roomsData.forEach(room => {
              const roomCourses = results.data
                .filter(course => {
                  if (room.building === 'MLU' || room.building === 'Martin Luther University College') {
                    const courseRoom = course.Room?.trim() || '';
                    const courseBuilding = course.Building?.trim() || '';
                    const isMLURoom = courseRoom.startsWith('MLU') || courseBuilding === 'MLU';
                    const roomNumber = courseRoom.startsWith('MLU') ? 
                      courseRoom.substring(3) : courseRoom;
                    
                    return isMLURoom && 
                           roomNumber === room.room &&
                           course.Days.includes(currentDay);
                  } else {
                    return (
                      course.Building === room.building && 
                      course.Room === room.room &&
                      course.Days.includes(currentDay)
                    );
                  }
                })
                .map(course => {
                  const [startTime, endTime] = course['Time Range'].split(' - ');
                  return {
                    ...course,
                    startMinutes: parseTimeToMinutes(startTime),
                    endMinutes: parseTimeToMinutes(endTime)
                  };
                })
                .sort((a, b) => a.startMinutes - b.startMinutes);

              let status = { isAvailable: true, nextClass: null };
              
              for (let i = 0; i < roomCourses.length; i++) {
                const course = roomCourses[i];
                if (timeToCheck < course.startMinutes) {
                  const availableTime = course.startMinutes - timeToCheck;
                  if (selectedTimeRange) {
                    if (timeToCheck + duration > course.startMinutes) {
                      status.isAvailable = false;
                      status.nextClass = course;
                    } else {
                      status.nextClass = course;
                    }
                  } else if (availableTime >= 20) {
                    status.nextClass = course;
                  } else {
                    status.isAvailable = false;
                    status.nextClass = course;
                  }
                  break;
                } else if (timeToCheck >= course.startMinutes && timeToCheck < course.endMinutes) {
                  status.isAvailable = false;
                  status.currentClass = course;
                  status.nextClass = roomCourses[i + 1] || null;
                  break;
                }
              }

              roomAvailability[room.id] = status;
            });

            setAvailability(roomAvailability);
          }
        });
      } catch (error) {
        console.error("Error fetching rooms", error);
      }
      setLoading(false);
    };

    fetchRoomsAndSchedules();
  }, [building, selectedTimeRange]);

  const getRoomStatus = (roomId) => {
    const status = availability[roomId];
    if (!status) return { text: 'Loading...', color: 'gray' };

    if (status.isAvailable) {
      if (status.nextClass) {
        const statusText = selectedTimeRange ? 
          'Available for selected time' : 
          `Available until ${formatTimeDisplay(status.nextClass.startMinutes)}`;
        return { text: statusText, color: 'green' };
      }
      return {
        text: selectedTimeRange ? 'Available for selected time' : 'Available all day',
        color: 'green'
      };
    }

    if (status.currentClass) {
      return {
        text: `Occupied until ${formatTimeDisplay(status.currentClass.endMinutes)}`,
        color: 'red'
      };
    }

    if (status.nextClass) {
      return {
        text: selectedTimeRange ? 'Unavailable' : 
              `Next class at ${formatTimeDisplay(status.nextClass.startMinutes)}`,
        color: 'yellow'
      };
    }

    return { text: 'Checking...', color: 'gray' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex justify-center items-center">
        <div className="w-8 h-8 border-4 border-white/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  const sortedRooms = [...rooms].sort((a, b) => {
    const roomA = parseRoomNumber(a.room);
    const roomB = parseRoomNumber(b.room);
    return roomA.localeCompare(roomB);
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">{decodeURIComponent(building)}</h1>
              <p className="text-gray-400 text-sm">Real-time classroom availability</p>
            </div>
          </div>

          {selectedTimeRange && (
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full">
              <Clock className="w-4 h-4 text-purple-400" />
              <span className="text-purple-200 text-sm font-medium">
                {formatTimeDisplay(selectedTimeRange.start)} - {formatTimeDisplay(selectedTimeRange.start + selectedTimeRange.duration)}
              </span>
              <button
                onClick={() => {
                  setSelectedTimeRange(null);
                  localStorage.removeItem('selectedTimeRange');
                }}
                className="ml-2 text-purple-400 hover:text-white transition-colors"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <ClassroomTable
          rooms={sortedRooms}
          getRoomStatus={getRoomStatus}
          building={building}
          navigate={navigate}
        />
      </div>
    </div>
  );
};

export default ClassroomsByBuilding;
